import type { CollapseProps, TableProps } from 'antd';
import { Button, Table } from 'antd';
import { COLOR_OPTIONS } from 'config/style/color-options';
import type React from 'react';
import SvgEdit2 from 'st-protector-icons/Minor/Edit2';
import SvgExpandLess from 'st-protector-icons/navigation/expand-less';
import SvgExpandMore from 'st-protector-icons/navigation/expand-more';
import { StyledCollapse, StyledHeaderContainer, StyledHeaderEditButton, StyledHeaderTitle } from './collapsable-table.styles';

interface CollapsableTableItem<T extends object> {
  title: React.ReactNode;
  key: string;
  onEditTitle?: () => void;
  tableColumns: TableProps<T>['columns'];
  tableDataSource: T[];
}

interface CollapsableTableProps<T extends object> {
  items: CollapsableTableItem<T>[];
  defaultActive?: boolean;
  dataTestId?: string;
  rowSelection?: TableProps<T>['rowSelection'];
}

export const CollapsableTable = <T extends object>({ items, defaultActive = true, dataTestId, rowSelection }: CollapsableTableProps<T>) => {
  const renderExpandIcon = (isActive?: boolean) => (isActive ? <SvgExpandLess /> : <SvgExpandMore />);

  const handleOnEditTitle = (element: React.MouseEvent<HTMLElement>, onEditTitle?: () => void) => {
    element.stopPropagation();
    onEditTitle?.();
  };

  const collapseItems: CollapseProps['items'] = items.map(item => ({
    key: item.key,
    label: (
      <StyledHeaderContainer>
        <StyledHeaderTitle>{item.title}</StyledHeaderTitle>
        {item.onEditTitle && (
          <StyledHeaderEditButton>
            <Button
              data-testid='collapsable-table-header-edit-button'
              type='link'
              tabIndex={0}
              onClick={e => handleOnEditTitle(e, item.onEditTitle)}
              icon={<SvgEdit2 fill={COLOR_OPTIONS.NEUTRAL_00} />}
            />
          </StyledHeaderEditButton>
        )}
      </StyledHeaderContainer>
    ),
    children: <Table columns={item.tableColumns} dataSource={item.tableDataSource} pagination={false} rowSelection={rowSelection} />
  }));

  return (
    <StyledCollapse
      data-testid={dataTestId}
      defaultActiveKey={defaultActive ? items.map(item => item.key) : []}
      expandIcon={({ isActive }) => renderExpandIcon(isActive)}
      items={collapseItems}
    />
  );
};
